import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { Plus, Trash2, Info, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import { Select } from '../../ui/newSelect';
import BrandSelect from '../../ui/BrandSelect';
import { adminToolbarSelect } from '../../utils/ReactSelectStyles';
import {
  BILLING_CURRENCY_OPTIONS,
  symbolToStripeCurrency,
  resolveBillingCurrencySymbol,
  formatOrganisationCurrency,
} from '../../utils/stripeCurrency';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { Alert, AlertDescription } from '../../ui/alert';
import Breadcrumb from '../../Common/Breadcrumb';
import ConsultantPricingSchedule from '../../Agreements/ConsultantPricingSchedule';
import { PartnerTermsForm, AmbassadorTermsForm } from '../../Agreements/CommercialTerms';
import { Actions as AGREEMENTS } from '../../../store/actions/agreementsActions';
import { Actions as ADMINACTIONS, getAllItemsFromDB } from '../../../store/actions/adminActions';
import { Actions as ORGANISATIONACTIONS } from '../../../store/actions/organisationActions';
import { Actions as LEGAL } from '../../../store/actions/legalActions';
import {
  buildAgreementCountryPayload,
  buildContractingEntityQueryParams,
  formatCyfContractingEntity,
} from '../../Modals/agreementsUtils';
import { toggleAlertMessage } from '../../../store/action';
import { contractToFormState, isEditableAgreementStatus } from './agreementFormUtils';

// One-off fee lines only; `unitAmount` is in MAJOR units (e.g. `500` = £500.00).
// Subscription lines are built server-side from the Plan × FTE catalogue.
const defaultOneOffLine = () => ({
  kind: 'one_off',
  unitAmount: '',
  quantity: 1,
  description: '',
});

const titleCase = (s) => String(s || '').replace(/\b\w/g, (c) => c.toUpperCase());

const roundMajor = (n) => Math.round(Number(n) * 100) / 100;

const hasVariation = (v) =>
  !!v && ((v.agreedUnitAmount != null && v.agreedUnitAmount !== '') || (v.discountPercent != null && v.discountPercent !== ''));

/** Agreed amount after a sales variation: an explicit price wins over a discount %. */
const agreedAmount = (list, v) => {
  if (list == null) return null;
  if (!hasVariation(v)) return list;
  if (v.agreedUnitAmount != null && v.agreedUnitAmount !== '') return roundMajor(v.agreedUnitAmount);
  return roundMajor(list * (1 - Number(v.discountPercent) / 100));
};

const VARIATION_DURATION_OPTIONS = [
  { value: 'term', label: 'Whole term' },
  { value: 'first_year', label: 'First year only' },
];

const PriceVariationInputs = ({ symbol, list, value, onChange }) => {
  const v = value || {};
  const agreed = agreedAmount(list, v);
  const aboveList = hasVariation(v) && list != null && agreed > list;
  return (
    <div className="grid grid-cols-3 gap-2">
      <div>
        <Label className="text-xs">Discount %</Label>
        <Input
          type="number"
          min="0"
          max="100"
          step="0.01"
          placeholder="0"
          value={v.discountPercent ?? ''}
          onChange={(e) => onChange({ discountPercent: e.target.value, agreedUnitAmount: '' })}
        />
      </div>
      <div>
        <Label className="text-xs">Agreed price ({symbol})</Label>
        <Input
          type="number"
          min="0"
          step="0.01"
          placeholder={list != null ? String(list) : ''}
          value={v.agreedUnitAmount ?? ''}
          onChange={(e) => onChange({ agreedUnitAmount: e.target.value, discountPercent: '' })}
        />
      </div>
      <div>
        <Label className="text-xs">Applies for</Label>
        <Select
          value={v.duration || 'term'}
          onChange={(d) => onChange({ duration: d })}
          options={VARIATION_DURATION_OPTIONS}
          disabled={!hasVariation(v)}
        />
      </div>
      {hasVariation(v) && list != null && (
        <p className="col-span-3 text-xs text-muted-foreground">
          Customer pays {formatAmount(symbol, agreed)} / year (list {formatAmount(symbol, list)}
          {aboveList ? ', above list' : ''})
          {v.duration === 'first_year' ? '; list price applies from the first renewal.' : '.'}
        </p>
      )}
    </div>
  );
};

const formatAmount = (symbol, amount) =>
  amount == null || Number.isNaN(Number(amount))
    ? '—'
    : `${symbol}${Number(amount).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// Backend canonicalises this; we mirror it so we can pre-empt `type_mismatch` 400s.
const canonicaliseAccountType = (raw) => {
  if (!raw) return '';
  if (raw === 'White Label Pending') return 'White Label';
  if (raw === 'Ambassador' || raw === 'White Label' || raw === 'Consultant' || raw === 'Direct') return raw;
  return '';
};

const ERROR_CODE_MESSAGES = {
  catalog_price_required: 'Subscription pricing must come from the Plan × FTE catalogue.',
  catalog_price_unavailable: 'That Plan and FTE band has no catalogue price in this currency.',
  catalog_addon_unavailable: 'One of the selected add-ons has no catalogue price in this currency.',
  catalog_selection_incomplete: 'Select a Plan and FTE band.',
  unsupported_currency:
    'That billing currency is not supported. Use £ / $ / € / AUD.',
  line_item_currency_mismatch:
    'A line item currency does not match the contract currency.',
  invalid_lineItem_unitAmount: 'Each one-off fee needs a numeric amount.',
  invalid_price_variation: 'A discount must be over 0% and at most 100%, and an agreed price must be 0 or more.',
  invalid_payMode: 'Pay mode is invalid.',
  invalid_lineItem_kind: 'Line item kind must be subscription or one_off.',
  invalid_term_type: 'Term type must be committed or rolling.',
  invalid_customerOrganisationId: 'The linked organisation id is invalid.',
  invalid_replaceExistingSubscription:
    'The replace-existing-subscription flag must be a boolean.',
  unknown_organisation:
    "We could not find that organisation. Pick a different one or switch to 'Create new'.",
  organisation_pending_agreement:
    'This organisation already has a pending agreement. Resend or void the existing invite first.',
  type_mismatch:
    "Type does not match the linked organisation's account type. Adjust the type to match.",
  no_published_legal_documents:
    'No legal documents are published yet. Publish at least one ToS / privacy version before creating agreements.',
  jurisdiction_schedule_unavailable:
    'Published jurisdiction schedule is missing or invalid. Contact CYF admin.',
  consultant_pricing_schedule_unavailable:
    'The Consultant Pricing Schedule is incomplete for this currency. Configure every Plan × FTE and active Add-on price before sending the Agreement.',
  price_variation_reason_required: 'Give a reason for varying from the list price.',
  invalid_partner_terms:
    'Check the Partner Reseller terms: licence fee 0 or more, discount 0–100%, a 12 or 36 month commitment, and a start date on the first of a month.',
  invalid_ambassador_terms:
    'Check the Ambassador terms: each share over 0% and at most 100%, durations of 1–10 years (indefinite only last), and a GBP or EUR payout currency.',
  catalog_currency_unavailable: 'Partner Account Fees need a complete catalogue in this currency (GBP or EUR).',
  self_billing_agreement_unpublished:
    'Publish the self-billing agreement legal document before sending an Ambassador Agreement with revenue share.',
};

/** Shape `indexationPolicy` for POST /my/agreements — only fields relevant to each kind. */
const buildIndexationPolicy = (policy) => {
  if (!policy || policy.kind === 'none') return { kind: 'none' };
  const noticeDays = Number(policy.noticeDays) || 0;
  const floorPercent = Number(policy.floorPercent) || 0;
  if (policy.kind === 'fixed_percent') {
    return { kind: 'fixed_percent', floorPercent, noticeDays };
  }
  if (policy.kind === 'cpi_with_floor') {
    return {
      kind: 'cpi_with_floor',
      floorPercent,
      cpiSource: policy.cpiSource || 'UK_CPIH',
      noticeDays,
    };
  }
  return policy;
};

const friendlyErrorMessage = (result) => {
  if (!result) return 'Failed to create agreement';
  const raw = result?.error;
  const code = typeof raw === 'string' ? raw : raw?.code;
  if (code && ERROR_CODE_MESSAGES[code]) return ERROR_CODE_MESSAGES[code];
  return result?.message || 'Failed to create agreement';
};

const NewAgreement = ({
  createAgreement,
  voidAgreement,
  getAgreementContract,
  agreementsList,
  listOrganisationId,
  countries,
  showMessage,
  adminOrgs,
  whiteLabelAdminOrgs,
  currencies,
  getAdminOrganisations,
  getWLAdminOrgs,
  getConsultantPricingSchedule,
  loadCurrencies,
  listCountries,
  getContractingEntity,
  currentOrganisation,
}) => {
  const history = useHistory();
  const location = useLocation();
  const { id: routeId } = useParams();
  const isEdit = location.pathname.endsWith('/edit');
  const editId = isEdit ? routeId : null;
  const customerOrgId =
    new URLSearchParams(location.search).get('org') || listOrganisationId || '';
  const [saving, setSaving] = useState(false);
  const [editLoading, setEditLoading] = useState(!!editId);
  const [editLoadError, setEditLoadError] = useState(null);
  // 'link' = pick an existing organisation; 'create' = create a brand-new one.
  const [customerMode, setCustomerMode] = useState('link');
  const [linkedOrgId, setLinkedOrgId] = useState('');
  const [replaceExistingSubscription, setReplaceExistingSubscription] = useState(false);
  const [billingCountryDiffers, setBillingCountryDiffers] = useState(false);
  const [contractingPreview, setContractingPreview] = useState(null);
  const [contractingLoading, setContractingLoading] = useState(false);
  const [contractingPreviewError, setContractingPreviewError] = useState(null);
  const [pricingSchedule, setPricingSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [form, setForm] = useState({
    type: 'White Label',
    customerOrganisationName: '',
    customerFirstName: '',
    customerLastName: '',
    customerEmail: '',
    customerRegisteredCountry: '',
    customerBillingCountry: '',
    billingCurrency: '£',
    whiteLabelId: '0',
    consultantId: 'NONE',
    endClientDefaultPlanType: 'catalog_plan',
    perReportFeeVal: '',
    annualPortfolioMinimum: '',
    startingProgrammeLevel: '',
    payMode: 'invoice',
    catalogPlanId: 'measure',
    fteBand: '1-10',
    addOnIds: [],
    priceVariations: {},
    priceVariationReason: '',
    partnerTerms: {
      licenceFee: '',
      licenceFeeFrequency: 'annual',
      discountPercent: '',
      minimumCommitment: '',
      termMonths: 12,
      startDate: '',
    },
    ambassadorTerms: { revenueSharePeriods: [], minimumPortfolio: '', payoutCurrency: 'GBP' },
    lineItems: [],
    term: {
      type: 'committed',
      lengthMonths: 36,
      startDate: '',
      noticePeriodDays: 60,
    },
    indexationPolicy: {
      kind: 'none',
      floorPercent: 5,
      cpiSource: 'UK_CPIH',
      noticeDays: 30,
    },
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateTerm = (k, v) => setForm((f) => ({ ...f, term: { ...f.term, [k]: v } }));
  const updateIdx = (k, v) => setForm((f) => ({ ...f, indexationPolicy: { ...f.indexationPolicy, [k]: v } }));

  useEffect(() => {
    if (!currentOrganisation) return undefined;
    let cancelled = false;
    setScheduleLoading(true);
    getConsultantPricingSchedule(form.billingCurrency).then((schedule) => {
      if (!cancelled) {
        setPricingSchedule(schedule);
        setScheduleLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [form.billingCurrency, currentOrganisation, getConsultantPricingSchedule]);

  useEffect(() => {
    if (form.type !== 'Consultant') return;
    setForm((current) => {
      const alreadyConsultantTerm = current.term.type === 'committed' && Number(current.term.noticePeriodDays) === 180;
      const months = Number(current.term.lengthMonths);
      return {
        ...current,
        payMode: editId ? current.payMode : 'checkout',
        term: {
          ...current.term,
          type: 'committed',
          lengthMonths: alreadyConsultantTerm && Number.isFinite(months) && months >= 12 ? months : 12,
          noticePeriodDays: 180,
        },
      };
    });
  }, [form.type, editId]);

  useEffect(() => {
    if (!editId || !customerOrgId) {
      if (editId) setEditLoadError('Missing customer organisation. Open edit from the agreements list.');
      setEditLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setEditLoading(true);
      setEditLoadError(null);
      const contract = await getAgreementContract(editId, customerOrgId);
      if (cancelled) return;
      if (!contract) {
        setEditLoadError('Agreement not found for this organisation.');
        setEditLoading(false);
        return;
      }
      if (!isEditableAgreementStatus(contract.status)) {
        setEditLoadError(`Agreement status "${contract.status}" cannot be edited.`);
        setEditLoading(false);
        return;
      }
      const mapped = contractToFormState(contract);
      if (mapped) {
        setForm((f) => ({
          ...f,
          ...mapped,
        }));
        if (mapped.linkedExistingOrganisation && mapped.linkedOrganisationId) {
          setCustomerMode('link');
          setLinkedOrgId(String(mapped.linkedOrganisationId));
          setReplaceExistingSubscription(!!mapped.replaceExistingSubscription);
        } else {
          setCustomerMode('create');
        }
        if (
          mapped.customerBillingCountry &&
          mapped.customerRegisteredCountry &&
          mapped.customerBillingCountry !== mapped.customerRegisteredCountry
        ) {
          setBillingCountryDiffers(true);
        }
      }
      setEditLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [editId, customerOrgId, getAgreementContract]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!adminOrgs || !adminOrgs.length) {
      getAdminOrganisations();
    }
    if (!whiteLabelAdminOrgs || !whiteLabelAdminOrgs.length) {
      getWLAdminOrgs();
    }
    if (!currencies || !currencies.length) {
      loadCurrencies();
    }
    if (!countries || !countries.length) {
      listCountries();
    }
  }, [
    adminOrgs,
    whiteLabelAdminOrgs,
    currencies,
    countries,
    getAdminOrganisations,
    getWLAdminOrgs,
    loadCurrencies,
    listCountries,
  ]);

  const effectiveBillingCountryId = billingCountryDiffers
    ? form.customerBillingCountry
    : form.customerRegisteredCountry;

  useEffect(() => {
    const inc = form.customerRegisteredCountry;
    const bill = effectiveBillingCountryId;
    if (!inc && !bill) {
      setContractingPreview(null);
      setContractingPreviewError(null);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setContractingLoading(true);
      setContractingPreviewError(null);
      const params = buildContractingEntityQueryParams({
        incorporationCountryId: inc,
        billingCountryId: bill,
        countries,
      });
      const result = await getContractingEntity(params);
      if (cancelled) return;
      setContractingLoading(false);
      if (result?.error) {
        setContractingPreview(null);
        setContractingPreviewError(
          result.error === 'no_entity_rows'
            ? 'Could not resolve a contracting entity from the jurisdiction schedule.'
            : result.error === 'not_authorised'
              ? 'Sign in required to preview the contracting entity.'
              : 'Could not load contracting entity preview.',
        );
        return;
      }
      setContractingPreview(result?.entity || null);
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    form.customerRegisteredCountry,
    effectiveBillingCountryId,
    countries,
    getContractingEntity,
  ]);

  const contractIsoCurrency = useMemo(
    () => symbolToStripeCurrency(form.billingCurrency),
    [form.billingCurrency]
  );

  const planOptions = useMemo(
    () => (pricingSchedule?.plans || []).map((id) => ({ value: id, label: titleCase(id) })),
    [pricingSchedule]
  );
  const fteOptions = useMemo(
    () => (pricingSchedule?.fteBands || []).map((id) => ({ value: id, label: `${id} FTE` })),
    [pricingSchedule]
  );

  const is36MonthTerm = Number(form.term.lengthMonths) === 36;
  const planStandardPrice = pricingSchedule?.matrix?.[form.catalogPlanId]?.[form.fteBand]?.standard ?? null;
  const planNetPrice =
    planStandardPrice == null
      ? null
      : is36MonthTerm
        ? Math.round(planStandardPrice * 0.95 * 100) / 100
        : planStandardPrice;

  const addOnRows = useMemo(
    () =>
      (pricingSchedule?.addOns || [])
        .filter((a) => a.standardAnnualPrice != null)
        .map((a) => ({ ...a, included: (a.includedPlanIds || []).includes(form.catalogPlanId) })),
    [pricingSchedule, form.catalogPlanId]
  );

  const selectedAddOns = useMemo(
    () => addOnRows.filter((a) => !a.included && (form.addOnIds || []).includes(a.id)),
    [addOnRows, form.addOnIds]
  );

  const activeVariations = useMemo(() => {
    const out = {};
    const pv = form.priceVariations || {};
    if (hasVariation(pv.plan)) out.plan = pv.plan;
    selectedAddOns.forEach((a) => {
      if (hasVariation(pv[a.id])) out[a.id] = pv[a.id];
    });
    return out;
  }, [form.priceVariations, selectedAddOns]);

  const annualSubscriptionTotal = useMemo(() => {
    if (planNetPrice == null) return null;
    const addOns = selectedAddOns.reduce(
      (sum, a) => sum + agreedAmount(Number(a.standardAnnualPrice), activeVariations[a.id]),
      0
    );
    return roundMajor(agreedAmount(planNetPrice, activeVariations.plan) + addOns);
  }, [planNetPrice, selectedAddOns, activeVariations]);

  const setPriceVariation = (key, patch) =>
    setForm((f) => ({
      ...f,
      priceVariations: { ...(f.priceVariations || {}), [key]: { ...((f.priceVariations || {})[key] || {}), ...patch } },
    }));

  const toggleAddOn = (addOnId) =>
    setForm((f) => {
      const set = new Set(f.addOnIds || []);
      if (set.has(addOnId)) set.delete(addOnId);
      else set.add(addOnId);
      return { ...f, addOnIds: [...set] };
    });

  const orgOptions = useMemo(
    () =>
      (adminOrgs || []).map((o) => ({
        value: o.id,
        label: o.name || o.id,
        org: o,
      })),
    [adminOrgs]
  );

  const orgValue = useMemo(
    () => orgOptions.find((opt) => opt.value === linkedOrgId) || null,
    [orgOptions, linkedOrgId]
  );

  const linkedOrg = orgValue?.org || null;
  const linkedAccountType = canonicaliseAccountType(linkedOrg?.accountType);
  const typeIsLocked = customerMode === 'link' && !!linkedAccountType;
  const linkedOrgIsConsultantParent =
    !!linkedOrg?.id &&
    !!linkedOrg?.consultantId &&
    String(linkedOrg.id) === String(linkedOrg.consultantId);

  // Convention used elsewhere in the admin (see ModalPropsAdminPortal.js):
  // a consultant "parent" org is one whose row's id equals its own consultantId.
  const consultantParentOptions = useMemo(
    () =>
      (adminOrgs || [])
        .filter((o) => o.id && o.consultantId && o.id === o.consultantId)
        .map((o) => ({ value: o.id, label: o.name || o.id })),
    [adminOrgs]
  );

  const wlParentOptions = useMemo(
    () =>
      (whiteLabelAdminOrgs || []).map((o) => ({
        value: o.whiteLabelId || o.id,
        label: o.name || o.whiteLabelId || o.id,
      })),
    [whiteLabelAdminOrgs]
  );

  // Reset the parent ids when the type changes so we don't carry stale values.
  useEffect(() => {
    setForm((f) => {
      const next = { ...f };
      if (f.type !== 'White Label' && f.whiteLabelId !== '0') next.whiteLabelId = '0';
      if (f.type !== 'Consultant' && f.consultantId !== 'NONE') next.consultantId = 'NONE';
      return next;
    });
  }, [form.type]);

  // Linked org IS the consultant — no separate parent picker; it references itself.
  useEffect(() => {
    if (form.type !== 'Consultant') return;
    if (customerMode === 'create') {
      setForm((f) => (f.consultantId === 'NONE' ? f : { ...f, consultantId: 'NONE' }));
      return;
    }
    if (linkedOrgIsConsultantParent && linkedOrgId) {
      setForm((f) => (f.consultantId === linkedOrgId ? f : { ...f, consultantId: linkedOrgId }));
    }
  }, [form.type, customerMode, linkedOrgIsConsultantParent, linkedOrgId]);

  // Lock the type to the linked org's account type to pre-empt `type_mismatch` 400s.
  useEffect(() => {
    if (typeIsLocked && form.type !== linkedAccountType) {
      update('type', linkedAccountType);
    }
  }, [typeIsLocked, linkedAccountType]); // eslint-disable-line react-hooks/exhaustive-deps

  const onBillingCurrencyChange = (symbol) =>
    setForm((f) => ({ ...f, billingCurrency: symbol, addOnIds: [] }));

  const onPickOrg = (opt) => {
    if (!opt) {
      setLinkedOrgId('');
      setReplaceExistingSubscription(false);
      return;
    }
    const org = opt.org || {};
    const suggestedBilling = resolveBillingCurrencySymbol(org.currency, currencies);
    setLinkedOrgId(opt.value);
    const orgCountryId = org.countryId || org.country || '';
    setForm((f) => ({
      ...f,
      customerOrganisationName: org.name || f.customerOrganisationName,
      customerRegisteredCountry: orgCountryId || f.customerRegisteredCountry,
      customerBillingCountry: '',
      billingCurrency: suggestedBilling || f.billingCurrency,
      consultantId:
        f.type === 'Consultant' && org.id && org.consultantId && String(org.id) === String(org.consultantId)
          ? org.id
          : f.consultantId,
    }));
    setBillingCountryDiffers(false);
  };

  const setLine = (i, k, v) => {
    setForm((f) => {
      const li = f.lineItems.map((row, j) => (j === i ? { ...row, [k]: v } : row));
      return { ...f, lineItems: li };
    });
  };

  const addLine = () => setForm((f) => ({ ...f, lineItems: [...f.lineItems, defaultOneOffLine()] }));
  const removeLine = (i) => setForm((f) => ({ ...f, lineItems: f.lineItems.filter((_, j) => j !== i) }));

  // -- pre-submit validation ---------------------------------------------------
  const validate = () => {
    if (customerMode === 'link' && !linkedOrgId) {
      return 'Pick an existing organisation or switch to "Create new"';
    }
    if (form.type === 'Consultant' && customerMode === 'link' && !linkedOrgIsConsultantParent) {
      if (!form.consultantId || form.consultantId === 'NONE') {
        return 'Pick the consultant parent org, or link the consultant organisation itself (not an end client).';
      }
    }
    if (!pricingSchedule && form.type !== 'Ambassador') {
      return 'The catalogue is unavailable for this currency. Choose a complete catalogue currency.';
    }
    if (!contractIsoCurrency) {
      return 'Billing currency must be £, $, €, or AUD.';
    }
    if (!form.customerRegisteredCountry) {
      if (customerMode === 'create') {
        return 'Select place of incorporation.';
      }
      return 'Linked organisation has no country on file — set place of incorporation before saving.';
    }
    if (billingCountryDiffers && !form.customerBillingCountry) {
      return 'Select billing country.';
    }

    if (form.type === 'Consultant') {
      const months = Number(form.term.lengthMonths);
      if (!Number.isInteger(months) || months < 12) {
        return 'A Consultant agreement is at least 12 months.';
      }
      if (months > 12) {
        const minimum = Number(form.annualPortfolioMinimum);
        if (form.annualPortfolioMinimum === '' || !Number.isFinite(minimum) || minimum <= 0) {
          return 'A term longer than 12 months needs an annual portfolio minimum. That minimum stays for every year of the term.';
        }
      }
      return null;
    }

    if (form.type === 'Ambassador') {
      const terms = form.ambassadorTerms || {};
      const periods = terms.revenueSharePeriods || [];
      for (let i = 0; i < periods.length; i++) {
        const pct = Number(periods[i].percent);
        if (!(pct > 0 && pct <= 100)) return `Revenue share period ${i + 1} needs a share over 0% and at most 100%.`;
      }
      if (terms.minimumPortfolio !== '' && terms.minimumPortfolio != null && !(Number.isInteger(Number(terms.minimumPortfolio)) && Number(terms.minimumPortfolio) >= 1)) {
        return 'Minimum portfolio must be a whole number of at least 1.';
      }
      return null;
    }

    if (form.type === 'White Label') {
      const terms = form.partnerTerms || {};
      if (terms.licenceFee === '' || !(Number(terms.licenceFee) >= 0)) return 'Enter the Partner Licence Fee (0 for none).';
      if (terms.scheduleMode === 'custom_packages') {
        const pkgs = Array.isArray(terms.customPackages) ? terms.customPackages : [];
        if (!pkgs.length) return 'Add at least one White Label package, or switch to catalogue discount.';
        for (const p of pkgs) {
          if (!String(p.name || '').trim()) return 'Every White Label package needs a name.';
          if (!(p.fteBands || []).length || (p.fteBands || []).length !== (p.annualFees || []).length) {
            return `Package "${p.name || '?'}" needs matching FTE bands and CYF fees.`;
          }
          if (p.unlocksSwc && !(p.fteBands || []).length) return 'SWC packages require FTE bands.';
        }
      } else {
        const d = terms.discountPercent === '' ? 0 : Number(terms.discountPercent);
        if (!(d >= 0 && d <= 100)) return 'The Partner discount must be between 0% and 100%.';
      }
      if (terms.startDate && !String(terms.startDate).endsWith('-01')) return 'The Partner start date must be the first of a month.';
    } else {
      if (!form.catalogPlanId || !form.fteBand) return 'Select a Plan and FTE band.';
      if (planStandardPrice == null) {
        return 'That Plan and FTE band has no catalogue price in this currency.';
      }
      for (const v of Object.values(activeVariations)) {
        if (v.agreedUnitAmount !== '' && v.agreedUnitAmount != null) {
          if (!(Number(v.agreedUnitAmount) >= 0)) return 'Agreed prices must be 0 or more.';
        } else {
          const pct = Number(v.discountPercent);
          if (!(pct > 0 && pct <= 100)) return 'Discounts must be greater than 0% and at most 100%.';
        }
      }
      if (Object.keys(activeVariations).length && !String(form.priceVariationReason || '').trim()) {
        return 'Give a reason for varying from the list price.';
      }
    }
    for (let i = 0; i < form.lineItems.length; i++) {
      const row = form.lineItems[i];
      if (!String(row.description || '').trim()) return `One-off fee ${i + 1} needs a description.`;
      const amount = Number(row.unitAmount);
      if (!Number.isFinite(amount) || amount <= 0) return `One-off fee ${i + 1} needs an amount greater than 0.`;
    }
    return null;
  };

  // -- submit ------------------------------------------------------------------
  const onSubmit = async (e) => {
    e.preventDefault();

    const err = validate();
    if (err) {
      showMessage(true, err, 'error', 5000);
      return;
    }

    setSaving(true);

    const lineItems = (form.type === 'Consultant' || form.type === 'Ambassador' ? [] : form.lineItems).map((row) => ({
      kind: 'one_off',
      quantity: Number(row.quantity) || 1,
      description: String(row.description || '').trim(),
      unitAmount: Number(row.unitAmount) || 0,
      currency: contractIsoCurrency,
    }));

    const body = {
      type: form.type,
      customerOrganisationName: form.customerOrganisationName,
      customerFirstName: form.customerFirstName,
      customerLastName: form.customerLastName,
      customerEmail: form.customerEmail,
      ...buildAgreementCountryPayload({
        incorporationCountryId: form.customerRegisteredCountry,
        billingCountryId: effectiveBillingCountryId,
        countries,
      }),
      billingCurrency: form.billingCurrency,
      whiteLabelId: form.whiteLabelId,
      consultantId: form.consultantId,
      payMode: form.payMode,
      lineItems,
      term: { ...form.term, lengthMonths: Number(form.term.lengthMonths) || 0, noticePeriodDays: Number(form.term.noticePeriodDays) || 0 },
      indexationPolicy: buildIndexationPolicy(form.indexationPolicy),
    };

    if (form.type === 'White Label') {
      const terms = form.partnerTerms || {};
      body.payMode = 'invoice';
      body.partnerTerms = {
        licenceFee: Number(terms.licenceFee) || 0,
        licenceFeeFrequency: terms.licenceFeeFrequency || 'annual',
        accountFeeCadence: terms.accountFeeCadence || 'monthly',
        scheduleMode: terms.scheduleMode === 'custom_packages' ? 'custom_packages' : 'catalogue_discount',
        discountPercent: terms.discountPercent === '' ? 0 : Number(terms.discountPercent),
        customPackages: terms.scheduleMode === 'custom_packages' ? terms.customPackages || [] : null,
        minimumCommitment: terms.minimumCommitment === '' || terms.minimumCommitment == null ? null : Number(terms.minimumCommitment),
        termMonths: Number(terms.termMonths) || 12,
        ...(terms.startDate ? { startDate: terms.startDate } : {}),
      };
    } else if (form.type === 'Ambassador') {
      const terms = form.ambassadorTerms || {};
      body.payMode = 'invoice';
      body.ambassadorTerms = {
        revenueSharePeriods: (terms.revenueSharePeriods || []).map((p) => ({
          percent: Number(p.percent),
          years: p.years === 'indefinite' ? 'indefinite' : Number(p.years),
        })),
        minimumPortfolio: terms.minimumPortfolio === '' || terms.minimumPortfolio == null ? null : Number(terms.minimumPortfolio),
        payoutCurrency: terms.payoutCurrency || 'GBP',
      };
    } else if (form.type !== 'Consultant') {
      const priceVariations = Object.fromEntries(
        Object.entries(activeVariations).map(([key, v]) => [
          key,
          {
            ...(v.agreedUnitAmount !== '' && v.agreedUnitAmount != null
              ? { agreedUnitAmount: Number(v.agreedUnitAmount) }
              : { discountPercent: Number(v.discountPercent) }),
            duration: v.duration || 'term',
          },
        ])
      );
      body.catalogSelection = {
        planId: form.catalogPlanId,
        fteBand: form.fteBand,
        addOnIds: selectedAddOns.map((a) => a.id),
        ...(Object.keys(priceVariations).length
          ? { priceVariations, priceVariationReason: String(form.priceVariationReason).trim() }
          : {}),
      };
    }

    if (customerMode === 'link' && linkedOrgId) {
      body.customerOrganisationId = linkedOrgId;
      body.replaceExistingSubscription = !!replaceExistingSubscription;
    }

    if (form.type === 'Consultant') {
      if (form.annualPortfolioMinimum !== '' && form.annualPortfolioMinimum != null) {
        body.annualPortfolioMinimum = Number(form.annualPortfolioMinimum);
        if (form.startingProgrammeLevel) {
          body.startingProgrammeLevel = form.startingProgrammeLevel;
        }
      }
    }

    if (editId) {
      const voidR = await voidAgreement(
        editId,
        { reason: 'Superseded by revised order form' },
        customerOrgId
      );
      if (!voidR?.ok) {
        setSaving(false);
        showMessage(true, voidR?.message || 'Could not void the previous agreement', 'error', 6000);
        return;
      }
    }

    const r = await createAgreement(body);
    setSaving(false);
    if (r?.ok) {
      showMessage(
        true,
        editId ? 'Revised agreement created and invite sent' : 'Agreement created and invite sent',
        'success',
        4000
      );
      if (r.data?.agreementUrl) {
        window.open(r.data.agreementUrl, '_blank', 'noopener');
      }
      history.push(
        customerOrgId
          ? `/admin/agreements?org=${encodeURIComponent(customerOrgId)}`
          : '/admin/agreements'
      );
    } else {
      showMessage(true, friendlyErrorMessage(r), 'error', 6000);
    }
  };

  const countryOptions = (countries || []).map((c) => ({ value: c.id, label: c.name }));
  const contractingEntityDisplay = useMemo(
    () => formatCyfContractingEntity(contractingPreview),
    [contractingPreview],
  );
  const showContractingPreview =
    Boolean(form.customerRegisteredCountry || effectiveBillingCountryId);

  if (editId && editLoading) {
    return (
      <div className="flex justify-center p-12">
        <Loader2 className="h-10 w-10 animate-spin" />
      </div>
    );
  }

  if (editId && editLoadError) {
    return (
      <div className="p-6 max-w-lg space-y-3">
        <p>{editLoadError}</p>
        <Button onClick={() => history.push('/admin/agreements')}>Back to agreements</Button>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <Breadcrumb
        items={[
          { label: 'Agreements', link: '/admin/agreements' },
          { label: editId ? 'Edit agreement' : 'New agreement' },
        ]}
      />
      {editId && (
        <Alert className="mb-4">
          <AlertDescription className="text-xs">
            Saving will void the current unsigned agreement and create a revised order form with a
            new invite link.
          </AlertDescription>
        </Alert>
      )}
      <form onSubmit={onSubmit} className="space-y-6">
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-3">
            <CardTitle>Customer</CardTitle>
            <Tabs value={customerMode} onValueChange={editId ? () => {} : setCustomerMode}>
              <TabsList>
                <TabsTrigger value="link">Link existing</TabsTrigger>
                <TabsTrigger value="create">Create new</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {customerMode === 'link' ? (
              <div className="md:col-span-2 space-y-3">
                <div>
                  <Label>Existing organisation *</Label>
                  <BrandSelect
                    classNamePrefix="cyf-select"
                    options={orgOptions}
                    value={orgValue}
                    isClearable
                    isSearchable
                    placeholder={
                      !adminOrgs || !adminOrgs.length
                        ? 'Loading organisations...'
                        : 'Search organisations'
                    }
                    onChange={onPickOrg}
                    styles={adminToolbarSelect}
                    menuPosition="fixed"
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                  />
                  {orgValue && (
                    <Alert className="mt-2">
                      <AlertDescription className="flex items-start gap-2 text-xs">
                        <Info size={14} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                        <span>
                          <strong>{orgValue.label}</strong>
                          {linkedOrg?.country ? ` — ${linkedOrg.country}` : ''}
                          {linkedOrg?.currency
                            ? ` (org currency: ${formatOrganisationCurrency(linkedOrg.currency, currencies)})`
                            : ''}
                          {linkedAccountType ? ` · type: ${linkedAccountType}` : ''}
                          . The new agreement will be linked to this org via{' '}
                          <code>customerOrganisationId</code> instead of creating a new one.
                        </span>
                      </AlertDescription>
                    </Alert>
                  )}
                </div>

                {orgValue && (
                  <div className="flex items-start gap-2 rounded-md border border-border p-2">
                    <Checkbox
                      id="replace-sub"
                      checked={replaceExistingSubscription}
                      onCheckedChange={(v) => setReplaceExistingSubscription(!!v)}
                    />
                    <div className="text-xs">
                      <Label htmlFor="replace-sub" className="cursor-pointer">
                        Replace existing subscription (renewal)
                      </Label>
                      <p className="text-muted-foreground">
                        Cancels the org's current Stripe subscription and archives sibling
                        contracts when this agreement is provisioned. Leave off for add-on
                        contracts that should coexist (e.g. Reports add-on).
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div>
                  <Label>Organisation name *</Label>
                  <Input
                    value={form.customerOrganisationName}
                    onChange={(e) => update('customerOrganisationName', e.target.value)}
                    required
                  />
                </div>
              </>
            )}

            <div className="md:col-span-2">
              <Label>Place of incorporation *</Label>
              <Select
                value={form.customerRegisteredCountry}
                onChange={(v) => update('customerRegisteredCountry', v)}
                options={countryOptions}
                placeholder="Select country"
                searchable
                clearable
              />
            </div>

            <div className="md:col-span-2 flex items-start gap-2 rounded-md border border-border p-2">
              <Checkbox
                id="billing-country-differs"
                checked={billingCountryDiffers}
                onCheckedChange={(v) => {
                  setBillingCountryDiffers(!!v);
                  if (!v) update('customerBillingCountry', '');
                }}
              />
              <div className="text-xs">
                <Label htmlFor="billing-country-differs" className="cursor-pointer">
                  Billing country differs from incorporation
                </Label>
                <p className="text-muted-foreground">
                  When unchecked, billing country matches incorporation. If they differ,
                  incorporation governs which CYF entity contracts.
                </p>
              </div>
            </div>

            {billingCountryDiffers && (
              <div className="md:col-span-2">
                <Label>Billing country *</Label>
                <Select
                  value={form.customerBillingCountry}
                  onChange={(v) => update('customerBillingCountry', v)}
                  options={countryOptions}
                  placeholder="Select billing country"
                  searchable
                  clearable
                />
              </div>
            )}

            <div>
              <Label>Billing currency *</Label>
              <Select
                value={form.billingCurrency}
                onChange={onBillingCurrencyChange}
                options={BILLING_CURRENCY_OPTIONS}
                placeholder="Select billing currency"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Invoice currency for this agreement — independent of the linked org&apos;s stored
                currency. Plan, add-on and one-off amounts are in this currency.
              </p>
            </div>

            <div>
              <Label>Type *</Label>
              <Select
                value={form.type}
                onChange={(v) => {
                  update('type', v);
                  if (v === 'Direct') update('payMode', 'checkout');
                }}
                options={['Ambassador', 'White Label', 'Consultant', 'Direct']}
                placeholder="Type"
                disabled={typeIsLocked}
              />
              {typeIsLocked && (
                <p className="text-xs text-muted-foreground mt-1">
                  Locked to the linked organisation's type ({linkedAccountType}).
                </p>
              )}
            </div>
            <div>
              <Label>Signer first name *</Label>
              <Input value={form.customerFirstName} onChange={(e) => update('customerFirstName', e.target.value)} required />
            </div>
            <div>
              <Label>Signer last name *</Label>
              <Input value={form.customerLastName} onChange={(e) => update('customerLastName', e.target.value)} required />
            </div>
            <div>
              <Label>Signer email *</Label>
              <Input type="email" value={form.customerEmail} onChange={(e) => update('customerEmail', e.target.value)} required />
            </div>
            {form.type === 'White Label' && (
              <div className="md:col-span-2">
                <Label>Existing white label settings (optional)</Label>
                <BrandSelect
                  classNamePrefix="cyf-select"
                  options={wlParentOptions}
                  value={wlParentOptions.find((o) => o.value === form.whiteLabelId) || null}
                  isClearable
                  isSearchable
                  placeholder={
                    !whiteLabelAdminOrgs || !whiteLabelAdminOrgs.length
                      ? 'Loading white label partners...'
                      : 'Search white label partners'
                  }
                  onChange={(opt) => update('whiteLabelId', opt ? opt.value : '0')}
                  styles={adminToolbarSelect}
                  menuPosition="fixed"
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                />
              </div>
            )}
            {form.type === 'Consultant' && (
              <div className="md:col-span-2">
                {linkedOrgIsConsultantParent ? (
                  <Alert className="mb-3">
                    <AlertDescription className="text-xs">
                      <strong>{linkedOrg?.name}</strong> is the consultant organisation — it does not need a separate
                      parent. End-client billing is configured below.
                    </AlertDescription>
                  </Alert>
                ) : customerMode === 'create' ? (
                  <p className="text-xs text-muted-foreground mb-3">
                    A new consultant org will reference itself as parent when the agreement is accepted.
                  </p>
                ) : (
                  <>
                    <Label>Consultant parent *</Label>
                    <BrandSelect
                      classNamePrefix="cyf-select"
                      options={consultantParentOptions}
                      value={consultantParentOptions.find((o) => o.value === form.consultantId) || null}
                      isClearable
                      isSearchable
                      placeholder={
                        !consultantParentOptions.length
                          ? 'No consultant parent orgs found'
                          : 'Search consultant parent orgs'
                      }
                      onChange={(opt) => update('consultantId', opt ? opt.value : 'NONE')}
                      styles={adminToolbarSelect}
                      menuPosition="fixed"
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Only needed when the customer org above is not the consultant itself (unusual).
                    </p>
                  </>
                )}
              </div>
            )}
            {(form.type === 'Direct' || form.type === 'Ambassador') && (
              <div className="md:col-span-2 text-xs text-muted-foreground">
                Direct / Ambassador agreements stay on Compare Your Footprint
                (<code>whiteLabelId = '0'</code>, <code>consultantId = 'NONE'</code>) — no parent picker needed.
              </div>
            )}
            {form.type === 'White Label' && (
              <div className="md:col-span-2 text-xs text-muted-foreground">
                A Partner Reseller Agreement. When linked to an existing white label Partner, its current licence and
                per-report billing continues until the day before the reseller start date.
              </div>
            )}
          </CardContent>
        </Card>

        {form.type === 'Consultant' && (
          scheduleLoading ? (
            <Card>
              <CardContent className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading the Consultant Pricing Schedule that will be incorporated into this Agreement…
              </CardContent>
            </Card>
          ) : pricingSchedule ? (
            <>
              <ConsultantPricingSchedule schedule={pricingSchedule} />
              <Card>
                <CardHeader>
                  <CardTitle>Annual portfolio minimum (optional)</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Annual portfolio minimum ({form.billingCurrency})</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Leave empty for no minimum"
                      value={form.annualPortfolioMinimum}
                      onChange={(e) => update('annualPortfolioMinimum', e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Leave empty when there is no agreed floor. The contract and PDF will state that there is no
                      annual minimum.
                    </p>
                  </div>
                  <div>
                    <Label>Starting programme level</Label>
                    <Select
                      value={form.startingProgrammeLevel || ''}
                      onValueChange={(v) => update('startingProgrammeLevel', v)}
                      placeholder="None"
                      disabled={form.annualPortfolioMinimum === ''}
                      options={[
                        { value: '', label: 'None' },
                        { value: 'foundation', label: 'Foundation' },
                        { value: 'growth', label: 'Growth' },
                        { value: 'scale', label: 'Scale' },
                      ]}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Only applies when a minimum is entered for the commitment year.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Alert variant="destructive">
              <AlertDescription>
                No complete Consultant Pricing Schedule is available for {form.billingCurrency}. This Agreement cannot
                be sent until every Plan × FTE and Add-on price is configured.
              </AlertDescription>
            </Alert>
          )
        )}

        {showContractingPreview && (
          <Card>
            <CardHeader>
              <CardTitle>CYF contracting party</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              {contractingLoading && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Resolving contracting entity…
                </div>
              )}
              {!contractingLoading && contractingPreviewError && (
                <Alert>
                  <AlertDescription className="text-xs">{contractingPreviewError}</AlertDescription>
                </Alert>
              )}
              {!contractingLoading && contractingEntityDisplay && (
                <div className="space-y-1">
                  <p className="font-semibold">{contractingEntityDisplay.title}</p>
                  {contractingEntityDisplay.lines.map((line) => (
                    <p key={line} className="text-muted-foreground">
                      {line}
                    </p>
                  ))}
                  {contractingEntityDisplay.isDefault && (
                    <p className="text-xs text-muted-foreground italic">
                      Default jurisdiction (no specific row matched).
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {form.type === 'White Label' && (
          <PartnerTermsForm
            value={form.partnerTerms}
            onChange={(v) => update('partnerTerms', v)}
            schedule={pricingSchedule}
            symbol={form.billingCurrency}
          />
        )}

        {form.type === 'Ambassador' && (
          <AmbassadorTermsForm value={form.ambassadorTerms} onChange={(v) => update('ambassadorTerms', v)} />
        )}

        {form.type === 'Direct' && (
          <Card>
            <CardHeader>
              <CardTitle>Plan &amp; price</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {scheduleLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading the catalogue…
                </div>
              ) : !pricingSchedule ? (
                <Alert variant="destructive">
                  <AlertDescription>
                    No complete catalogue is available for {form.billingCurrency}. Choose another billing currency.
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Plan *</Label>
                      <Select
                        value={form.catalogPlanId}
                        onChange={(v) => update('catalogPlanId', v)}
                        options={planOptions}
                      />
                    </div>
                    <div>
                      <Label>FTE band *</Label>
                      <Select value={form.fteBand} onChange={(v) => update('fteBand', v)} options={fteOptions} />
                    </div>
                    <div>
                      <Label>Annual License Price</Label>
                      <p className="text-lg font-semibold text-foreground mt-1">
                        {formatAmount(form.billingCurrency, planNetPrice)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {is36MonthTerm
                          ? `Catalogue ${formatAmount(form.billingCurrency, planStandardPrice)} less 5% for a 36-month term.`
                          : 'Catalogue price, excluding VAT. A 36-month term takes 5% off.'}
                      </p>
                    </div>
                  </div>

                  <div className="md:w-1/2">
                    <PriceVariationInputs
                      symbol={form.billingCurrency}
                      list={planNetPrice}
                      value={form.priceVariations?.plan}
                      onChange={(patch) => setPriceVariation('plan', patch)}
                    />
                  </div>

                  {addOnRows.length > 0 && (
                    <div className="space-y-2">
                      <Label>Add-ons</Label>
                      {addOnRows.map((a) => {
                        const selected = !a.included && (form.addOnIds || []).includes(a.id);
                        return (
                          <div key={a.id} className="space-y-2">
                            <label className="flex items-center gap-2 text-sm text-foreground">
                              <Checkbox
                                checked={a.included || selected}
                                disabled={a.included}
                                onCheckedChange={() => toggleAddOn(a.id)}
                              />
                              <span className="flex-1">{a.name}</span>
                              <span className="text-muted-foreground">
                                {a.included
                                  ? `Included in ${titleCase(form.catalogPlanId)}`
                                  : `${formatAmount(form.billingCurrency, a.standardAnnualPrice)} / year`}
                              </span>
                            </label>
                            {selected && (
                              <div className="md:w-1/2 pl-6">
                                <PriceVariationInputs
                                  symbol={form.billingCurrency}
                                  list={Number(a.standardAnnualPrice)}
                                  value={form.priceVariations?.[a.id]}
                                  onChange={(patch) => setPriceVariation(a.id, patch)}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {Object.keys(activeVariations).length > 0 && (
                    <div>
                      <Label>Reason for varying from list price *</Label>
                      <Input
                        value={form.priceVariationReason}
                        onChange={(e) => update('priceVariationReason', e.target.value)}
                        placeholder="e.g. Multi-site launch partner, agreed on call 10 Oct"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Recorded on the agreement with your name. The order form shows the list price and the agreed
                        price.
                      </p>
                    </div>
                  )}

                  <div className="flex items-center justify-between border-t border-border pt-3 text-sm">
                    <span className="text-muted-foreground">Annual subscription (excl. VAT)</span>
                    <span className="font-semibold text-foreground">
                      {formatAmount(form.billingCurrency, annualSubscriptionTotal)}
                    </span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {(form.type === 'Direct' || form.type === 'White Label') && (
          <Card>
            <CardHeader>
              <CardTitle>One-off fees (optional)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {form.lineItems.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Add onboarding, development or other one-off charges invoiced with the first period.
                </p>
              )}
              {form.lineItems.map((row, i) => (
                <div
                  key={i}
                  className="grid grid-cols-1 md:grid-cols-6 gap-2 items-end border border-border p-2 rounded-md"
                >
                  <div className="md:col-span-3">
                    <Label className="text-xs">Description *</Label>
                    <Input value={row.description} onChange={(e) => setLine(i, 'description', e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Amount ({form.billingCurrency}) *</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={row.unitAmount}
                      onChange={(e) => setLine(i, 'unitAmount', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Qty</Label>
                    <Input
                      type="number"
                      min="1"
                      value={row.quantity}
                      onChange={(e) => setLine(i, 'quantity', e.target.value)}
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeLine(i)}>
                      <Trash2 size={16} strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addLine}>
                <Plus size={16} strokeWidth={1.5} className="mr-1" />
                Add one-off fee
              </Button>
            </CardContent>
          </Card>
        )}

        {form.type !== 'Ambassador' && (
        <Card>
          <CardHeader>
            <CardTitle>{form.type === 'White Label' ? 'Indexation' : 'Term & indexation'}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {form.type !== 'White Label' && (
            <>
            <div>
              <Label>Term type</Label>
              <Select
                value={form.term.type}
                onChange={(v) => updateTerm('type', v)}
                options={['committed', 'rolling']}
                disabled={form.type === 'Consultant'}
              />
            </div>
            <div>
              <Label>Length (months)</Label>
              <Input
                type="number"
                min={form.type === 'Consultant' ? '12' : undefined}
                step="1"
                value={form.term.lengthMonths}
                onChange={(e) => updateTerm('lengthMonths', e.target.value)}
              />
              {form.type === 'Consultant' && (
                <p className="text-xs text-muted-foreground mt-1">
                  12 months renews each year unless notice is given. A longer term runs to the end you enter, and the 180-day notice applies once at that end. Each following year, the annual portfolio minimum is the higher of the amount you enter and 80% of the previous year's catalogue charges, so it does not fall below that amount.
                </p>
              )}
            </div>
            <div>
              <Label>Start date</Label>
              <Input type="date" value={form.term.startDate} onChange={(e) => updateTerm('startDate', e.target.value)} />
            </div>
            <div>
              <Label>Notice (days)</Label>
              <Input
                type="number"
                value={form.term.noticePeriodDays}
                onChange={(e) => updateTerm('noticePeriodDays', e.target.value)}
                disabled={form.type === 'Consultant'}
              />
            </div>
            </>
            )}
            <div>
              <Label>Indexation kind</Label>
              <Select
                value={form.indexationPolicy.kind}
                onChange={(v) => updateIdx('kind', v)}
                options={['none', 'fixed_percent', 'cpi_with_floor']}
              />
            </div>
            {form.indexationPolicy.kind === 'fixed_percent' && (
              <>
                <div>
                  <Label>Annual increase %</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.1"
                    value={form.indexationPolicy.floorPercent}
                    onChange={(e) => updateIdx('floorPercent', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Fixed uplift applied each indexation anniversary (sent as{' '}
                    <code>floorPercent</code> on the contract).
                  </p>
                </div>
                <div>
                  <Label>Notice days</Label>
                  <Input
                    type="number"
                    value={form.indexationPolicy.noticeDays}
                    onChange={(e) => updateIdx('noticeDays', e.target.value)}
                  />
                </div>
              </>
            )}
            {form.indexationPolicy.kind === 'cpi_with_floor' && (
              <>
                <div>
                  <Label>Floor %</Label>
                  <Input
                    type="number"
                    value={form.indexationPolicy.floorPercent}
                    onChange={(e) => updateIdx('floorPercent', e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Minimum uplift when CPI is lower than this floor.
                  </p>
                </div>
                <div>
                  <Label>CPI source</Label>
                  <Select
                    value={form.indexationPolicy.cpiSource}
                    onChange={(v) => updateIdx('cpiSource', v)}
                    options={['UK_CPIH', 'UK_CPI', 'UK_RPI']}
                    placeholder="CPI source"
                  />
                </div>
                <div>
                  <Label>Notice days</Label>
                  <Input
                    type="number"
                    value={form.indexationPolicy.noticeDays}
                    onChange={(e) => updateIdx('noticeDays', e.target.value)}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
        )}

        {(form.type === 'Direct' || form.type === 'Consultant') && (
          <Card>
            <CardHeader>
              <CardTitle>Pay mode</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Select
                value={form.payMode}
                onChange={(v) => update('payMode', v)}
                options={[
                  { value: 'checkout', label: 'Card or direct debit' },
                  { value: 'invoice', label: 'Invoice' },
                  { value: 'both', label: 'Card or direct debit, plus a one-off invoice' },
                ]}
              />
              <p className="text-xs text-muted-foreground">
                {form.type === 'Consultant'
                  ? 'Card or direct debit still issues each sales invoice, then collects it. A saved card or direct debit is charged when the invoice is raised. If nothing is saved yet, that invoice includes a payment link, and paying it saves the method for the next one. Invoice means we issue the invoice and wait for a transfer.'
                  : 'You set this when you send the agreement. Card or direct debit is one payment page; the customer chooses the method there. Invoice means we invoice them and do not collect a card or a direct debit.'}
              </p>
            </CardContent>
          </Card>
        )}

        <div className="flex gap-2">
          <Button type="submit" loading={saving} disabled={saving}>
            {editId ? 'Save revised agreement & send invite' : 'Create & send invite'}
          </Button>
          <Button type="button" variant="outline" onClick={() => history.push('/admin/agreements')}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

const mapState = (s) => ({
  currentOrganisation: s?.currentOrganisation,
  countries: s?.organisation?.countries,
  agreementsList: s?.agreements?.list || [],
  listOrganisationId: s?.agreements?.listOrganisationId,
  adminOrgs: s?.admin?.organisations,
  whiteLabelAdminOrgs: s?.admin?.whiteLabelAdminOrgs,
  currencies:
    s?.admin?.adminPortal?.currencies?.length > 0
      ? s.admin.adminPortal.currencies
      : s?.organisation?.currencies,
});

const mapDispatch = (d) => ({
  createAgreement: (b) => d(AGREEMENTS.createAgreement(b)),
  voidAgreement: (id, body, oid) => d(AGREEMENTS.voidAgreement(id, body, oid)),
  getAgreementContract: (id, oid) => d(AGREEMENTS.getAgreementContract(id, oid)),
  getConsultantPricingSchedule: (currency) => d(AGREEMENTS.getConsultantPricingSchedule(currency)),
  getAdminOrganisations: () => d(ADMINACTIONS.getAdminOrganisations()),
  getWLAdminOrgs: () => d(ORGANISATIONACTIONS.getWLAdminOrgs()),
  loadCurrencies: () => d(getAllItemsFromDB('currencies')),
  listCountries: () => d(ORGANISATIONACTIONS.listCountries()),
  getContractingEntity: (params) => d(LEGAL.getContractingEntity(params)),
  showMessage: (...a) => d(toggleAlertMessage(...a)),
});

export default connect(mapState, mapDispatch)(NewAgreement);
